import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar } from "lucide-react";
import { motion } from "framer-motion";

const HeroSection = () => {
  const stats = [
    { value: 7, suffix: "+", label: "Years of", sublabel: "Experience" },
    { value: 20, suffix: "+", label: "Projects", sublabel: "Led" },
  ];

  const [displayedText, setDisplayedText] = useState("");
  const [headingText, setHeadingText] = useState("");
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [isHeadingTypingComplete, setIsHeadingTypingComplete] = useState(false);
  const fullText = "Hey, I'm Any";
  const headingFullText = "Senior Marketing Specialist based in Yerevan, Armenia.";

  // Typing animation effect for greeting
  useEffect(() => {
    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setDisplayedText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setIsTypingComplete(true);
      }
    }, 100);

    return () => clearInterval(typingInterval);
  }, []);

  // Typing animation effect for heading (starts after greeting completes)
  useEffect(() => {
    if (!isTypingComplete) return;
    
    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex <= headingFullText.length) {
        setHeadingText(headingFullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setIsHeadingTypingComplete(true);
      }
    }, 50);

    return () => clearInterval(typingInterval);
  }, [isTypingComplete]);

  const handleChat = () => {
    const element = document.getElementById("consultation");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };


  return (
    <section id="hero" className="min-h-[calc(100vh-4rem)] lg:min-h-screen flex flex-col justify-center pt-4 sm:pt-8 lg:pt-20 pb-8">
      {/* Profile & Intro - Side by side */}
      <div className="flex items-center gap-4 sm:gap-6 mb-6 sm:mb-8 animate-fade-up">
        <div className="relative animate-wave-hello">
          <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-2xl overflow-hidden border-2 border-primary/30 shadow-lg">
            <img 
              src="/profile-photo.png" 
              alt="Any Hakobyan - Senior Marketing Specialist and Consultant based in Yerevan, Armenia" 
              className="w-full h-full object-cover"
              fetchPriority="high"
              decoding="async"
            />
          </div>
          <div className="absolute -bottom-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded-full border-2 border-background animate-pulse" />
          {/* Waving hand emoji */}
          <div className="absolute -top-2 -right-2 text-2xl sm:text-3xl animate-hand-wave">
            👋
          </div>
        </div>
        
        {/* Hey, I'm Any - Typing effect */}
        <div className="flex items-center">
          <p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-display font-bold text-foreground">
            {displayedText}
            <span 
              className={`inline-block w-[3px] h-[1em] bg-primary ml-1 align-middle ${isTypingComplete ? 'animate-pulse' : 'animate-[blink_0.7s_infinite]'}`}
            />
          </p>
        </div>
      </div>

      {/* Main Heading - Typing effect */}
      <div className="mb-6 sm:mb-8 animate-fade-up animation-delay-100">
        <h1 className="text-3xl leading-snug sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-display font-bold text-foreground mb-4">
          {headingText}
          {!isHeadingTypingComplete && isTypingComplete && (
            <span 
              className="inline-block w-[3px] h-[1em] bg-primary ml-1 align-middle animate-[blink_0.7s_infinite]"
            />
          )}
        </h1>
      </div>

      {/* Animated Stats */}
      <div className="flex flex-wrap gap-6 sm:gap-8 md:gap-16 mb-6 sm:mb-8 animate-fade-up animation-delay-200">
        {stats.map((stat, index) => (
          <motion.div 
            key={index} 
            className="flex items-baseline gap-2 sm:gap-3"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ 
              duration: 0.5, 
              delay: 0.8 + index * 0.2,
              type: "spring",
              stiffness: 100
            }}
          >
            <AnimatedNumber value={stat.value} suffix={stat.suffix} delay={1 + index * 0.2} />
            <div className="text-base sm:text-lg text-muted-foreground">
              <p>{stat.label}</p>
              <p>{stat.sublabel}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Animated Tagline */}
      <motion.h2 
        className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-display text-muted-foreground mb-8 sm:mb-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.5 }}
      >
        <motion.span
          initial={{ backgroundSize: "0% 100%" }}
          animate={{ backgroundSize: "100% 100%" }}
          transition={{ duration: 1.5, delay: 2, ease: "easeOut" }}
          className="bg-gradient-to-r from-primary/20 to-transparent bg-no-repeat bg-left"
          style={{ backgroundPosition: "0 100%", paddingBottom: "2px" }}
        >
          Ready to give your brand the{" "}
          <motion.span
            initial={{ color: "hsl(var(--muted-foreground))" }}
            animate={{ color: "hsl(var(--primary))" }}
            transition={{ duration: 0.8, delay: 2.5 }}
            className="font-semibold"
          >
            spotlight
          </motion.span>{" "}
          it deserves?
        </motion.span>
      </motion.h2>

      {/* CTA Button */}
      <div className="flex flex-col gap-3 animate-fade-up animation-delay-300">
        <Button 
          onClick={handleChat}
          size="lg"
          className="text-base sm:text-lg px-8 sm:px-10 py-6 sm:py-7 group shadow-[0_0_20px_hsl(var(--primary)/0.4)] hover:shadow-[0_0_30px_hsl(var(--primary)/0.6)] transition-shadow w-fit"
          style={{
            animation: 'pulse-glow 3s ease-in-out infinite'
          }}
        >
          <Calendar className="mr-2 h-5 w-5" />
          Book Free Consultation
          <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
        </Button>
        <p className="text-sm text-muted-foreground ml-1">
          30 min • Free • No commitment
        </p>
      </div>
    </section>
  );
};

// Animated number component with counting effect
const AnimatedNumber = ({ value, suffix, delay }: { value: number; suffix: string; delay: number }) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    const startTimer = setTimeout(() => {
      setHasStarted(true);
    }, delay * 1000);

    return () => clearTimeout(startTimer);
  }, [delay]);

  useEffect(() => {
    if (!hasStarted) return;

    const duration = 1500; // 1.5 seconds
    const steps = 30;
    const stepDuration = duration / steps;
    const increment = value / steps;
    let currentStep = 0;

    const counter = setInterval(() => {
      currentStep++;
      if (currentStep >= steps) {
        setCount(value);
        clearInterval(counter);
      } else {
        setCount(Math.ceil(increment * currentStep));
      }
    }, stepDuration);

    return () => clearInterval(counter);
  }, [hasStarted, value]);

  return (
    <motion.span 
      className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-bold text-primary"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, delay }}
    >
      {count}{suffix}
    </motion.span>
  );
};

export default HeroSection;
